import { coreLayoutMain, rbp } from '@/constants/measured-scope'
import { useAuth } from '@/context/auth'
import { getConfig } from '@/lib/config'
import { getCookieValue } from '@/lib/cookie'
import { eventsTracker, UIElementNames } from '@/lib/eventsTracker'
import { setPartnerCookieByCode, setPartnerCookieById } from '@/lib/partners'
import { QUERY_APP, QUERY_PARTNER_ASSOCIATIONS } from '@/service/query'
import { CurrentUser } from '@/shared/api-generated-types'
import PageContent from '@/widgets/PageContent'
import PageFooter from '@/widgets/PageFooter'
import { Props as HeaderProps, useMenuRef } from '@/widgets/PageHeader'
import PageHeaderNext from '@/widgets/PageHeaderNext'
import { useMediaQuery } from '@react-hookz/web'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo } from 'react'
import { useQuery } from 'urql'

interface Props {
  children?: React.ReactNode | React.ReactNode[]
  footerVisible?: boolean
  headerProps?: Partial<HeaderProps>
  contentClassName?: string
  disablePageContentBottomPadding?: boolean
}

const { publicRuntimeConfig } = getConfig()
const BASE_URL = publicRuntimeConfig.website.url

const PageLayout = ({
  children,
  footerVisible = true,
  headerProps = {},
  contentClassName = '',
  disablePageContentBottomPadding,
}: Props): JSX.Element => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const isSmallDevice = useMediaQuery(`only screen and (max-width : ${rbp.smallTablet})`, {
    initializeWithValue: false,
  })
  const { openCustomMenu, closeCustomMenu } = useMenuRef()
  const { dispatch: authDispatch } = useAuth()
  const [{ data, fetching }] = useQuery({
    query: QUERY_APP,
    requestPolicy: 'cache-first',
  })

  const partnerCookie = getCookieValue('auth.partner')
  const isPartnerRoute = router.route === '/partner/[slug]'
  const [{ data: dataPartnerAssociations, fetching: fetchingPartnerAssociations }] = useQuery<{
    currentUser: CurrentUser
  }>({
    query: QUERY_PARTNER_ASSOCIATIONS,
    requestPolicy: 'cache-first',
    pause: isPartnerRoute && !!partnerCookie,
  })

  // Set Partner Cookie when set via UTM source
  useEffect(() => {
    if (!router.isReady) return
    let partnerCode = null
    for (const param in router.query) {
      if (param.toLowerCase() === 'utm_source') {
        partnerCode = router.query[param]
        break
      }
    }
    if (!partnerCode) return
    setPartnerCookieByCode(partnerCode)
      .then((res) => {
        if (res.success) {
          const url = new URL(window.location.href)
          for (const param of url.searchParams.keys()) {
            if (param.toLowerCase() === 'utm_source') {
              url.searchParams.delete(param)
            }
          }
          setTimeout(() => window.location.replace(url.toString()), 1000)
        }
      })
      .catch(() => {
        console.error('Failed to set partner cookie from UTM source')
      })
  }, [router.isReady])

  useEffect(() => {
    if (fetching) return
    if (data?.currentUser && data?.currentUser?.authenticatedUser) {
      authDispatch({
        type: 'UPDATE',
        payload: {
          user: {
            ...data.currentUser.authenticatedUser,
            fullName: `${data.currentUser.authenticatedUser.firstName} ${data.currentUser.authenticatedUser.lastName}`,
          },
          tenantId: data.currentUser.tenantId,
        },
      })
    }
    const timer = setTimeout(() => authDispatch({ type: 'READY', payload: { ready: true } }), 2500)
    return () => clearTimeout(timer)
  }, [data, fetching])

  // When partner cookie is expired or not found, regenerate it without having to visit partner landing page
  useEffect(() => {
    if (partnerCookie || fetchingPartnerAssociations || !dataPartnerAssociations) return

    if (
      !partnerCookie &&
      dataPartnerAssociations?.currentUser?.authenticatedUser?.partnerAssociations &&
      dataPartnerAssociations?.currentUser?.authenticatedUser?.partnerAssociations.length > 0
    ) {
      const primaryPartnerAssociation =
        dataPartnerAssociations?.currentUser?.authenticatedUser?.partnerAssociations.find(
          (association) => association?.isPrimary === true,
        )
      if (primaryPartnerAssociation) {
        setPartnerCookieById(primaryPartnerAssociation?.partner?.id)
          .then((res) => {
            console.log('setPartnerCookieById', res)
          })
          .catch(() => {
            console.error('Failed to set partner cookie from UTM source')
          })
      }
    }
  }, [dataPartnerAssociations, fetchingPartnerAssociations])

  // On non-partner routes, silently correct the partner cookie if it doesn't match the primary partner
  useEffect(() => {
    if (isPartnerRoute || fetchingPartnerAssociations || !dataPartnerAssociations || !partnerCookie) return

    const primaryPartnerAssociation =
      dataPartnerAssociations?.currentUser?.authenticatedUser?.partnerAssociations?.find(
        (association) => association?.isPrimary === true,
      )

    if (!primaryPartnerAssociation?.partner?.id) return

    try {
      const parsedCookie = JSON.parse(partnerCookie)
      if (parsedCookie.id !== primaryPartnerAssociation.partner.id) {
        setPartnerCookieById(primaryPartnerAssociation.partner.id)
          .then((res) => {
            console.log('setPartnerCookieById', res)
          })
          .catch(() => {
            console.error('Failed to correct partner cookie to primary partner')
          })
      }
    } catch {
      // Cookie parse failed, ignore
      console.error('Failed to parse partner cookie')
    }
  }, [dataPartnerAssociations, fetchingPartnerAssociations, isPartnerRoute])

  useEffect(() => {
    if (!!headerProps?.customMenu) openCustomMenu()
    else closeCustomMenu()
  }, [headerProps?.customMenu])

  const menuLinks = useMemo(() => {
    const links: JSX.Element[] = [
      <a
        href="https://share.hsforms.com/1NendfH52RSCvpOFkCq8D1A49suz"
        onClick={() => eventsTracker.handleOnClickAndTrackEvent(UIElementNames.LINK_SIDEBAR_MENU_HOST_SESSION)}
        target="_blank"
      >
        {t('menu.host')}
      </a>,
      <a
        href="https://www.getsetup.com/about-us/about-us"
        onClick={() => eventsTracker.handleOnClickAndTrackEvent(UIElementNames.LINK_SIDEBAR_MENU_ABOUT_US)}
        target="_blank"
      >
        {t('menu.about')}
      </a>,
      <a
        href="https://www.getsetup.com/about-us/careers"
        onClick={() => eventsTracker.handleOnClickAndTrackEvent(UIElementNames.LINK_SIDEBAR_MENU_CAREERS)}
        target="_blank"
      >
        {t('menu.careers')}
      </a>,
      <Link prefetch={false} href={`${BASE_URL}/support`}>
        {t('nav.help')}
      </Link>,
      <a
        href="https://www.getsetup.com/about-us/press"
        onClick={() => eventsTracker.handleOnClickAndTrackEvent(UIElementNames.LINK_SIDEBAR_MENU_PRESS)}
        target="_blank"
      >
        {t('menu.press')}
      </a>,
      <a href="https://www.getsetup.com/about-us/contact-us" target="_blank">
        {t('menu.partnerWithUs')}
      </a>,
      //Commented out until page is live
      // <Link href={`${BASE_URL}/careers`} onClick={() => eventsTracker.handleOnClickAndTrackEvent(UIElementNames.LINK_SIDEBAR_MENU_CAREERS)}>
      //     {t('menu.careers')}
      // </Link>,
    ]
    // if (!data?.currentUser?.authenticatedUser) {
    //   links.push(
    //     <Button
    //       size="lg"
    //       theme="secondary"
    //       label={t('cta.signUpFree')}
    //       href="/login"
    //       component="link"
    //       onClick={() => eventsTracker.handleOnClickAndTrackEvent(UIElementNames.BUTTON_SIDEBAR_MENU_SIGNUP)}
    //     />,
    //   )
    // } else {
    //   links.push(
    //     <Button
    //       size="md"
    //       theme="secondary"
    //       label="Logout"
    //       href="/logout"
    //       component="link"
    //       // Event tracking commented out until UIElementName added to names list;
    //       // onClick={() => eventsTracker.handleOnClickAndTrackEvent(UIElementNames.BUTTON_SIDEBAR_MENU_LOGOUT)}
    //     />,
    //   )
    // }

    return links
  }, [data, t])

  const policyLinks = useMemo(() => {
    return [
      <a
        href="https://www.getsetup.com/about-us/privacy-policy"
        onClick={() => eventsTracker.handleOnClickAndTrackEvent(UIElementNames.LINK_FOOTER_PRIVACY_POLICY)}
        target="_blank"
      >
        {t('menu.privacyPolicy')}
      </a>,
      <a
        href="https://www.getsetup.com/about-us/terms-of-service"
        onClick={() => eventsTracker.handleOnClickAndTrackEvent(UIElementNames.LINK_FOOTER_TERMS_CONDITIONS)}
        target="_blank"
      >
        {t('menu.termsConditions')}
      </a>,
    ]
  }, [menuLinks])

  const footerLinks = useMemo(() => {
    const links = [...menuLinks]
    if (isSmallDevice) {
      return [...links, ...policyLinks]
    }
    return links
  }, [menuLinks, policyLinks])

  return (
    <>
      <PageHeaderNext />
      <PageContent ceilingTargetNode={'.PageHeaderNext'}>
        <main className={`${contentClassName} ${disablePageContentBottomPadding ? '' : coreLayoutMain}`}>
          {children}
        </main>
      </PageContent>
      {footerVisible && (
        <PageFooter
          copyrightText={`GetSetUp. ${t('allRightsReserved')}`}
          links={footerLinks}
          policyLinks={isSmallDevice ? [] : policyLinks}
        />
      )}
      {/* TODO: HelpFloater temporarily replaced with FBMessenger, until we invest some effort
      into the content of HelpFloater and how it operates */}
      {/*
      {!menuActive && (
        <HelpFloater
          title={t('helpMenu.title')}
          label={t('nav.help')}
          items={[
            <Link href="/" passHref>
              <MenuOption sidePadding label={t('helpMenu.createAccount')} icon="person-plus" />
            </Link>,
            <Link href="/" passHref>
              <MenuOption sidePadding label={t('helpMenu.bookClass')} icon="play" />
            </Link>,
            <Link href="/" passHref>
              <MenuOption sidePadding label={t('helpMenu.cancelClass')} icon="cancel" />
            </Link>,
            <Link href="/" passHref>
              <MenuOption sidePadding label={t('helpMenu.chat')} icon="messenger" />
            </Link>,
          ]}
        />
      )}
      */}
    </>
  )
}

export default PageLayout
